<template>
    <v-bottom-sheet v-model="dialog" v-stack="{ open: dialog, close: hide }" :max-width="560" :persistent="loading"
        @input="onSheetInput" content-class="pfadd-dialog">
        <div class="pfadd-sheet">
            <div class="pfadd-header">
                <v-btn icon small class="pfadd-back" :disabled="loading" @click="hide">
                    <v-icon size="18">mdi-chevron-left</v-icon>
                </v-btn>
                <span class="pfadd-title">{{ $t('routines.add_routine') }}</span>
            </div>

            <div class="pfadd-body">
                <div class="ab-filters">
                    <kimsa-combobox :placeholder="$t('common.folder')" dense :items="folderOptions" item-text="label"
                        @change="folder = $event" @clear="folder = null" clearable ref="folders" />
                    <kimsa-text-field :placeholder="$t('common.search')" :value="search" @change="search = $event"
                        prepend-inner-icon="mdi-magnify" dense clearable no-elevation />
                </div>

                <div v-if="loadingSearch" class="ab-msg">
                    <v-progress-circular indeterminate size="22" :color="accentColor" />
                </div>
                <div v-else-if="routines.length === 0" class="ab-msg">{{ $t('routines.no_routines_founded') }}</div>

                <div v-else class="ab-list">
                    <div v-for="r in routines" :key="r.objectID" class="ab-row" :class="{ 'ab-row--sel': isSelected(r) }"
                        @click="toggle(r)">
                        <div class="ab-icon"><v-icon size="20" :color="accentColor">mdi-dumbbell</v-icon></div>
                        <div class="ab-info">
                            <div class="ab-name text-truncate">{{ r.name }}</div>
                            <div class="ab-meta">{{ routineMeta(r) }}</div>
                        </div>
                        <v-btn icon small class="ab-add">
                            <v-icon size="18">{{ isSelected(r) ? 'mdi-check' : 'mdi-plus' }}</v-icon>
                        </v-btn>
                    </div>
                </div>

                <kimsa-pagination v-if="pageCount > 1" class="mt-3" center @page="onPageChange" :value="page + 1"
                    :pageCount="pageCount" :total-visible="isMobile ? 4 : 7" />
            </div>

            <div class="pfadd-footer">
                <v-btn depressed block :color="accentColor" class="pfadd-confirm" :loading="loading"
                    :class="{ 'black--text': $vuetify.theme.dark }" :disabled="selected.length === 0 || loading" @click="confirm">
                    {{ $t('common.add') }}{{ selected.length ? ` (${selected.length})` : '' }}
                </v-btn>
            </div>
        </div>
    </v-bottom-sheet>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { RoutineIndex } from "@/utils/Algolia";
import { debounce } from "@/utils/Common";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaPagination from "@/components/common/KimsaPagination";

export default {
    name: "AddExistingRoutineDialog",
    components: { KimsaCombobox, KimsaTextField, KimsaPagination },
    props: {
        coachId: {
            type: String,
            default: null,
        },
        isSecondDialog: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            dialog: false,
            folder: null,
            search: '',
            routines: [],
            loadingSearch: false,
            page: 0,
            pageCount: 0,
            selected: [],
        }
    },
    computed: {
        ...mapState(['folders', 'globalFolders']),
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        folderOptions(vm = this) {
            return [...vm.globalFolders, ...vm.folders].filter(f => f.type === 'routines').map(f => ({ ...f, id: f.id, label: vm.folderLabel(f) })) || []
        },
    },
    async mounted() {
        const vm = this
        if (vm.folders.length === 0) await vm.bindFolders(vm.coachId)
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
    },
    methods: {
        ...mapActions(['bindFolders', 'bindGlobalFolders']),
        show() {
            const vm = this
            vm.selected = []
            vm.dialog = true
            vm.loadRoutines()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onSheetInput(val) {
            if (this.loading) return
            if (!val && this.dialog) this.hide()
        },
        reset() {
            const vm = this
            vm.folder = null
            vm.search = ''
            vm.routines = []
            vm.page = 0
            vm.pageCount = 0
            vm.selected = []
            if (vm.$refs.folders) vm.$refs.folders.model = null
        },
        isSelected(routine) {
            return this.selected.some(r => r.objectID === routine.objectID)
        },
        toggle(routine) {
            const vm = this
            const idx = vm.selected.findIndex(r => r.objectID === routine.objectID)
            if (idx === -1) vm.selected.push(routine)
            else vm.selected.splice(idx, 1)
        },
        confirm() {
            const vm = this
            if (vm.selected.length === 0) return
            vm.$emit('select', [...vm.selected])
        },
        routineMeta(routine) {
            const vm = this
            const blocks = routine.description?.blocks
            if (blocks) return `${blocks} ${vm.$tc('common.blocks', blocks).toLowerCase()}`
            const objective = routine.description?.objective
            return objective ? vm.routineObjectiveLabel(objective) || objective : ''
        },
        debouncedLoadRoutines: debounce(function () {
            this.loadRoutines()
        }, 500),
        async loadRoutines() {
            const vm = this
            if (!vm.coachId) return
            await vm.withLoading(async () => {
                let filter = `coachId: ${vm.coachId}`
                if (vm.folder) filter += ` AND folder: ${vm.folder.id}`
                const response = await RoutineIndex.search(vm.search, { filters: filter, hitsPerPage: 8, page: vm.page })
                vm.routines = response.hits
                vm.pageCount = response.nbPages || 0
            }, 'loadingSearch')
        },
        onPageChange(p) {
            const vm = this
            vm.page = Number(p) - 1
            vm.loadRoutines()
        },
    },
    watch: {
        folder() {
            this.page = 0
            this.loadRoutines()
        },
        search() {
            this.page = 0
            this.debouncedLoadRoutines()
        },
    },
}
</script>

<style scoped>
.pfadd-sheet {
    background: var(--v-surface-base);
    border-top: 1px solid var(--v-outline-base);
    border-radius: 20px 20px 0 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
    width: 100%;
}

.pfadd-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 16px 12px;
    border-bottom: 1px solid var(--v-outline-base);
    flex-shrink: 0;
}

.pfadd-back {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    flex-shrink: 0;
}

.pfadd-back .v-icon {
    color: inherit !important;
}

.pfadd-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pfadd-body {
    flex: 1;
    overflow-y: auto;
    padding: 14px 16px;
}

@media (max-width: 959px) {
    .pfadd-body::-webkit-scrollbar {
        display: none;
    }
}

.ab-filters {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 12px;
}

.ab-msg {
    text-align: center;
    padding: 28px 0;
    color: var(--v-txtMuted-base);
}

.ab-list {
    display: flex;
    flex-direction: column;
}

.ab-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px;
    border: 1px solid transparent;
    border-bottom: 1px solid var(--v-outline-base);
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
}

.ab-row:hover {
    background: var(--v-surface2-base);
}

.ab-row--sel {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    margin-bottom: 4px;
}

.theme--dark .ab-row--sel {
    background: rgba(204, 255, 51, .08);
    border-color: rgba(204, 255, 51, .4);
}

.theme--light .ab-row--sel {
    background: rgba(51, 153, 153, .08);
    border-color: rgba(51, 153, 153, .4);
}

.ab-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;
    border-radius: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.ab-info {
    flex: 1;
    min-width: 0;
}

.ab-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.ab-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-top: 2px;
}

.ab-add {
    flex-shrink: 0;
    background: var(--v-surface2-base) !important;
}

.theme--dark .ab-add {
    color: var(--v-primary-base) !important;
}

.theme--light .ab-add {
    color: var(--v-accent-base) !important;
}

.pfadd-footer {
    flex-shrink: 0;
    padding: 12px 16px calc(12px + var(--safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
}

.pfadd-confirm {
    height: 46px !important;
    border-radius: 12px !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    font-weight: 700;
}
</style>
